import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useSections, isSectionOn } from "@/hooks/useSections";
import { toast } from "sonner";
import { CheckCircle2, ChevronLeft, ChevronRight, AlertTriangle, Video, Save } from "lucide-react";

const STORE_KEY_PREFIX = "hawkam_assessment_v1:"; // per topic

const GATE_FIELDS = [
  ["organization", "Organization Name", "اسم المنشأة", true, "text"],
  ["country", "Country", "الدولة", true, "text"],
  ["contact_name", "Contact Name", "اسم جهة الاتصال", true, "text"],
  ["job_title", "Job Title", "المسمى الوظيفي", false, "text"],
  ["email", "Email Address", "البريد الإلكتروني", true, "email"],
  ["phone", "Phone Number", "رقم الهاتف", false, "tel"],
];
const WORDS_RE = /^[\p{L}\p{M}][\p{L}\p{M}\p{N}\s.\-&'’()]*$/u;

function validateGate(v) {
  const e = {};
  if (!WORDS_RE.test(v.organization.trim()) || v.organization.trim().length < 2) e.organization = 1;
  if (!WORDS_RE.test(v.country.trim()) || v.country.trim().length < 2) e.country = 1;
  if (!WORDS_RE.test(v.contact_name.trim()) || !/\s/.test(v.contact_name.trim()) || v.contact_name.trim().length < 3) e.contact_name = 1;
  if (v.job_title.trim() && !WORDS_RE.test(v.job_title.trim())) e.job_title = 1;
  if (!/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(v.email.trim())) e.email = 1;
  if (v.phone.trim() && !/^\+?[\d\s\-()]{7,20}$/.test(v.phone.trim())) e.phone = 1;
  return e;
}

function AssessmentGate({ lang, item, onBack }) {
  const ar = lang === "ar";
  const [v, setV] = useState({ organization: "", country: "", contact_name: "", job_title: "", email: "", phone: "" });
  const [errs, setErrs] = useState({});
  const [busy, setBusy] = useState(false);

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validateGate(v);
    setErrs(e);
    if (Object.keys(e).length) { toast.error(ar ? "يرجى إدخال بيانات صحيحة" : "Please enter valid information"); return; }
    setBusy(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assessment-start.php`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...v, lang, assessment_id: item.id }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (data.errors) setErrs(data.errors);
        toast.error(data.detail === "validation" ? (ar ? "بعض الحقول غير صحيحة" : "Some fields are invalid") : (data.detail || r.statusText));
        return;
      }
      window.location.href = data.url;
    } catch (err) { toast.error(String(err.message || err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="container-x py-12" data-testid="assessment-gate">
      <div className="max-w-2xl">
        <button type="button" onClick={onBack} className="text-sm text-gold mb-4" data-testid="assessment-gate-back">{ar ? "← اختيار استبيان آخر" : "← Choose another survey"}</button>
        <span className="inline-block text-xs font-bold tracking-wide px-2 py-1 rounded bg-navy text-gold mb-3">{item.tag}</span>
        <h1 className="text-3xl md:text-4xl text-navy">{ar ? item.title_ar || item.title_en : item.title_en || item.title_ar}</h1>
        <p className="text-navy/70 mt-4 font-semibold">{ar ? "المعلومات الأساسية" : "Basic Information"}</p>
        <p className="text-navy/60 mb-6">{ar ? "أخبرنا عن منشأتك" : "Tell us about your organization"}</p>
        <form onSubmit={submit} noValidate className="card-elev p-8 grid gap-5 md:grid-cols-2">
          {GATE_FIELDS.map(([k, en, arL, req, type]) => (
            <label key={k} className={`block ${k === "organization" ? "md:col-span-2" : ""}`}>
              <span className="text-sm text-navy/80">{ar ? arL : en}{req && <span className="text-red-600"> *</span>}</span>
              <input type={type} value={v[k]} required={req} data-testid={`assessment-gate-${k}`}
                     onChange={(e) => setV({ ...v, [k]: e.target.value })}
                     className={`mt-1 w-full rounded-md border px-3 py-2 bg-white ${errs[k] ? "border-red-500" : "border-navy/20"}`} />
              {errs[k] && <span className="text-xs text-red-600">{ar ? "قيمة غير صحيحة" : "Invalid value"}</span>}
            </label>
          ))}
          <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" onChange={() => {}} />
          <div className="md:col-span-2 flex items-center justify-between gap-4 pt-2">
            <span className="text-xs text-navy/50">{ar ? "تُحفظ بياناتك وفق سياسة الخصوصية." : "Your data is stored per our privacy policy."}</span>
            <button type="submit" disabled={busy} className="btn-gold" data-testid="assessment-gate-submit">
              {busy ? "…" : ar ? "ابدأ التقييم ←" : "Start Assessment →"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AssessmentList({ lang, items }) {
  const ar = lang === "ar";
  const [picked, setPicked] = useState(null);
  if (picked) return <AssessmentGate lang={lang} item={picked} onBack={() => setPicked(null)} />;
  return (
    <div className="container-x py-12" data-testid="assessment-list">
      <h1 className="text-3xl md:text-4xl text-navy">{ar ? "استبيانات التقييم" : "Assessment Surveys"}</h1>
      <p className="text-navy/60 mt-2 mb-8">{ar ? "اختر الاستبيان المناسب لنظامك أو دولتك." : "Choose the survey matching your regulation or country."}</p>
      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {items.map((it) => (
          <button key={it.id} type="button" onClick={() => setPicked(it)} data-testid={`assessment-item-${it.id}`}
                  className="card-elev p-6 text-start hover:-translate-y-0.5 transition-transform">
            <span className="inline-block text-xs font-bold tracking-wide px-2 py-1 rounded bg-navy text-gold">{it.tag}</span>
            <h2 className="text-lg text-navy mt-3">{ar ? it.title_ar || it.title_en : it.title_en || it.title_ar}</h2>
            {(ar ? it.description_ar : it.description_en) && <p className="text-sm text-navy/60 mt-2">{ar ? it.description_ar : it.description_en}</p>}
            <span className="inline-block mt-4 text-gold text-sm">{ar ? "ابدأ ←" : "Start →"}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Assessment() {
  const { t, lang } = useI18n();
  const sections = useSections();
  const [topics, setTopics] = useState([]);
  const [topicKey, setTopicKey] = useState(""); // empty = default assessment (all sections)
  const [schema, setSchema] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: "", email: "", company: "", phone: "" });
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [surveys, setSurveys] = useState(null);

  useEffect(() => {
    api.get("/assessments").then((r) => setSurveys((r.data || []).filter((a) => a && a.enabled && a.has_file)))
      .catch(() => setSurveys([]));
  }, []);

  useEffect(() => {
    api.get("/assessment/topics").then((r) => setTopics(r.data || [])).catch(() => {});
    api.get("/assessment/schema").then((r) => {
      setSchema(r.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Restore saved progress whenever the topic changes.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORE_KEY_PREFIX + (topicKey || "_all"));
      if (raw) {
        const p = JSON.parse(raw);
        if (p && typeof p === "object") {
          if (p.answers) setAnswers(p.answers);
          if (p.contact) setContact({ ...contact, ...p.contact });
          if (p.step != null) setStep(p.step);
          if (p.savedAt) setSavedAt(p.savedAt);
          return;
        }
      }
      setAnswers({}); setStep(0); setSavedAt(null);
    } catch (_e) { /* ignore */ }
    // Restart on topic change — reset progress.
  }, [topicKey]);

  // Persist progress on every change (debounced by React batching).
  useEffect(() => {
    if (loading || submitted) return;
    try {
      const ts = new Date().toISOString();
      localStorage.setItem(
        STORE_KEY_PREFIX + (topicKey || "_all"),
        JSON.stringify({ answers, contact, step, savedAt: ts }),
      );
      setSavedAt(ts);
    } catch (_e) { /* localStorage may be blocked */ }
  }, [answers, contact, step, topicKey, loading, submitted]);

  const clearProgress = () => {
    try { localStorage.removeItem(STORE_KEY_PREFIX + (topicKey || "_all")); } catch (_e) { /* noop */ }
    setAnswers({}); setStep(0); setSavedAt(null);
    toast.success(lang === "ar" ? "تم مسح التقدم" : "Progress cleared");
  };

  // Sections + one final contact step
  const totalSteps = schema.length + 1;
  const isLast = step === totalSteps - 1;
  const currentSection = schema[step];
  const valueMap = { yes: 1, partial: 0.5, no: 0 };

  // Weighted local score preview using question.weight
  const score = useMemo(() => {
    if (!schema.length) return 0;
    let earned = 0, total = 0;
    schema.forEach((s) => s.questions.forEach((q) => {
      if (answers[q.key] !== undefined) {
        earned += (valueMap[answers[q.key]] ?? 0) * (q.weight || 1);
        total += (q.weight || 1);
      }
    }));
    return total ? Math.round((earned / total) * 100) : 0;
  }, [answers, schema]);

  // Per-section score for gap findings on the result screen.
  const sectionScores = useMemo(() => {
    return schema.map((s) => {
      let earned = 0, total = 0, answered = 0;
      s.questions.forEach((q) => {
        if (answers[q.key] !== undefined) {
          earned += (valueMap[answers[q.key]] ?? 0) * (q.weight || 1);
          total += (q.weight || 1);
          answered++;
        }
      });
      return {
        key: s.key || s.id,
        name_ar: s.name_ar, name_en: s.name_en,
        pct: total ? Math.round((earned / total) * 100) : 0,
        answered, count: s.questions.length,
      };
    });
  }, [answers, schema]);

  const canNext = () => {
    if (isLast) return contact.name && contact.email;
    if (!currentSection) return false;
    return currentSection.questions.every((q) => answers[q.key]);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/assessment", {
        ...contact, answers,
        score, // client hint; server recomputes with weights
      });
      setSubmitted({ id: data.id, score: data.score ?? score });
      // Clear stored progress after successful submission.
      try { localStorage.removeItem(STORE_KEY_PREFIX + (topicKey || "_all")); } catch (_e) { /* noop */ }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  if (!isSectionOn(sections, "assessment")) return (
    <div className="container-x py-24 text-navy/60" data-testid="assessment-off">
      {lang === "ar" ? "هذا القسم غير متاح حالياً." : "This section is currently unavailable."}
    </div>
  );
  if (loading || surveys === null) return <div className="container-x py-24 text-navy/60" data-testid="assessment-loading">Loading…</div>;
  if (surveys.length) return <AssessmentList lang={lang} items={surveys} />;
  if (!schema.length) return (
    <div className="container-x py-24" data-testid="assessment-empty">
      <h1 className="text-3xl text-navy">{lang === "ar" ? "لا توجد أسئلة تقييم متاحة حالياً" : "No assessment questions configured yet"}</h1>
    </div>
  );

  if (submitted) {
    const gaps = sectionScores
      .filter((s) => s.pct < 70 && s.answered > 0)
      .sort((a, b) => a.pct - b.pct)
      .slice(0, 5);
    return (
      <div className="container-x py-16" data-testid="assessment-thank-you">
        <div className="max-w-3xl">
          <div className="card-elev p-10 md:p-12">
            <CheckCircle2 size={40} className="text-gold" />
            <h1 className="mt-4 text-3xl md:text-4xl text-navy">{t("common.thankYou")}</h1>
            <span className="divider-gold" />
            <p className="text-navy/75">
              {lang === "ar"
                ? `نتيجتك الأولية للحوكمة: ${submitted.score}%. النتيجة إرشادية وتخضع لمراجعة أعمق من فريقنا.`
                : `Indicative governance score: ${submitted.score}%. Result is preliminary and subject to deeper review by our team.`}
            </p>
            <div className="mt-6 h-3 bg-navy/10 rounded-full overflow-hidden">
              <div className="h-full bg-gold" style={{ width: `${submitted.score}%` }} />
            </div>
          </div>

          {/* Gap findings — reuses section-level scoring already computed. */}
          {gaps.length > 0 && (
            <div className="mt-8 card-elev p-8" data-testid="gap-findings">
              <div className="flex items-center gap-3">
                <AlertTriangle className="text-amber-600" size={22} />
                <h2 className="text-2xl text-navy">
                  {lang === "ar" ? "أهم الفجوات المرصودة" : "Priority gap findings"}
                </h2>
              </div>
              <p className="mt-2 text-navy/60 text-sm">
                {lang === "ar"
                  ? "الأقسام التي تقل نتيجتها عن 70% تستحق الأولوية في خطة التحسين."
                  : "Sections scoring under 70% deserve priority in your improvement plan."}
              </p>
              <ul className="mt-5 divide-y divide-navy/10">
                {gaps.map((g) => (
                  <li key={g.key} className="py-3 flex items-center gap-4"
                      data-testid={`gap-row-${g.key}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-navy font-medium truncate">
                        {lang === "ar" ? g.name_ar : g.name_en}
                      </div>
                      <div className="mt-1 h-1.5 bg-navy/10 rounded-full overflow-hidden">
                        <div className={`h-full ${g.pct < 40 ? "bg-red-500" : "bg-amber-500"}`}
                             style={{ width: `${Math.max(g.pct, 4)}%` }} />
                      </div>
                    </div>
                    <span className={`chip !py-0 !px-2 text-xs font-bold ${
                      g.pct < 40 ? "!bg-red-100 !text-red-800 !border-red-200"
                                : "!bg-amber-100 !text-amber-800 !border-amber-200"
                    }`}>{g.pct}%</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-8 card-elev p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
               data-testid="post-assessment-cta">
            <div>
              <div className="text-lg text-navy font-semibold">
                {lang === "ar" ? "احجز استشارة عن بُعد لمناقشة نتيجتك" : "Book a remote consultation to discuss your result"}
              </div>
              <div className="text-sm text-navy/65 mt-1">
                {lang === "ar"
                  ? "ربط الاستشارة تلقائياً بتقييمك حتى يستعرض المستشار الفجوات معك."
                  : "The consultation will be linked to your assessment so the consultant can walk through the gaps with you."}
              </div>
            </div>
            <Link to={`/consultation?assessment=${submitted.id}`}
                  className="btn-gold" data-testid="book-consultation">
              <Video size={14} /> {lang === "ar" ? "احجز استشارة" : "Book consultation"}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-x py-16" data-testid="assessment-page">
      <div className="max-w-3xl">
        <span className="eyebrow">{t("nav.assessment")}</span>
        <h1 className="mt-4 text-4xl md:text-5xl text-navy">
          {lang === "ar" ? "تقييم الحوكمة الذاتي" : "Self-Assessment"}
        </h1>
        <div className="text-xs uppercase tracking-widest text-navy/50 mt-1">
          {lang === "ar"
            ? "أداة تقييم كاملة — تختلف عن معاينة تقييم الحوكمة العامة"
            : "Full self-assessment tool — distinct from the public Governance Assessment preview"}
        </div>
        <span className="divider-gold" />
        {topics.length > 0 && (
          <div className="mt-4" data-testid="assessment-topics">
            <div className="text-xs uppercase tracking-widest text-navy/60 mb-3">
              {lang === "ar" ? "اختر مجال التقييم" : "Choose assessment topic"}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setTopicKey("")}
                className={`chip ${topicKey === "" ? "!bg-gold !text-navy !border-gold" : "!bg-white !text-navy !border-navy/20"}`}
                data-testid="topic-all"
              >
                {lang === "ar" ? "الشامل" : "General"}
              </button>
              {topics.map((tp) => (
                <button
                  key={tp.id}
                  onClick={() => setTopicKey(tp.key)}
                  className={`chip ${topicKey === tp.key ? "!bg-gold !text-navy !border-gold" : "!bg-white !text-navy !border-navy/20"}`}
                  data-testid={`topic-${tp.key}`}
                >
                  {lang === "ar" ? tp.name_ar : tp.name_en}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-10 max-w-3xl">
        <div className="flex items-center justify-between text-xs uppercase tracking-widest text-navy/60 mb-2">
          <span>{lang === "ar" ? `الخطوة ${step + 1} من ${totalSteps}` : `Step ${step + 1} of ${totalSteps}`}</span>
          <span>{Math.round(((step + 1) / totalSteps) * 100)}%</span>
        </div>
        <div className="h-1 bg-navy/10 rounded-full overflow-hidden">
          <div className="h-full bg-gold" style={{ width: `${((step + 1) / totalSteps) * 100}%` }} />
        </div>
        {savedAt && (
          <div className="mt-2 text-[11px] text-navy/50 inline-flex items-center gap-1"
               data-testid="assess-progress-saved">
            <Save size={11} className="text-emerald-600" />
            {lang === "ar" ? "تم حفظ التقدم تلقائياً" : "Progress auto-saved"}
            <button type="button" onClick={clearProgress}
                    className="ms-2 underline text-navy/40 hover:text-red-600"
                    data-testid="assess-clear-progress">
              {lang === "ar" ? "مسح" : "clear"}
            </button>
          </div>
        )}
      </div>

      <div className="mt-10 max-w-3xl card-elev p-8 md:p-10 animate-fade-up" key={step}>
        <h2 className="text-2xl text-navy">
          {isLast
            ? (lang === "ar" ? "معلومات التواصل" : "Contact Information")
            : (lang === "ar" ? currentSection.name_ar : currentSection.name_en)}
        </h2>
        <span className="divider-gold" />

        {isLast ? (
          <div className="space-y-6">
            <input data-testid="assess-name" className="field-box" placeholder={t("common.name")} value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
            <input data-testid="assess-email" className="field-box" placeholder={t("common.email")} type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
            <input data-testid="assess-company" className="field-box" placeholder={t("common.company")} value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} />
            <input data-testid="assess-phone" className="field-box" placeholder={t("common.phone")} value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
            <div className="text-navy/70 text-sm border border-navy/10 bg-cream p-4 rounded-md">
              {lang === "ar" ? "النتيجة الأولية بناءً على إجاباتك:" : "Preliminary score based on your answers:"}
              <span className="ms-2 text-gold text-2xl font-serif">{score}%</span>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {currentSection.questions.map((q) => (
              <div key={q.key} data-testid={`assess-q-${q.key}`}>
                <p className="text-navy mb-3">{lang === "ar" ? q.q_ar : q.q_en}</p>
                <div className="flex gap-3 flex-wrap">
                  {[
                    { v: "yes", ar: "نعم", en: "Yes" },
                    { v: "partial", ar: "جزئياً", en: "Partial" },
                    { v: "no", ar: "لا", en: "No" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      data-testid={`assess-${q.key}-${opt.v}`}
                      type="button"
                      onClick={() => setAnswers({ ...answers, [q.key]: opt.v })}
                      className={`px-5 py-2 rounded-sm text-sm border transition-colors ${
                        answers[q.key] === opt.v
                          ? "bg-navy text-cream border-navy"
                          : "bg-white text-navy border-navy/20 hover:border-gold"
                      }`}
                    >
                      {lang === "ar" ? opt.ar : opt.en}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-10 flex items-center justify-between">
          <button data-testid="assess-back" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} className="text-navy/70 disabled:opacity-30 inline-flex items-center gap-2">
            <ChevronLeft size={16} /> {t("common.back")}
          </button>
          {isLast ? (
            <button data-testid="assess-submit" disabled={!canNext() || submitting} onClick={submit} className="btn-gold disabled:opacity-40">
              {submitting ? "..." : t("common.submit")}
            </button>
          ) : (
            <button data-testid="assess-next" disabled={!canNext()} onClick={() => setStep((s) => s + 1)} className="btn-gold disabled:opacity-40">
              {t("common.next")} <ChevronRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
